import {
  ChoicePicker,
  ChoicePickerPanel,
  ChoicePickerTrigger,
  type ChoiceItem,
} from "@/components/molecules/choice-picker";
import { IntegrationLogo } from "@/components/molecules/integration-logo";
import { Label } from "@/components/ui/label";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { ExternalAccountsAPI } from "@/features/external-accounts/api/external-accounts";
import { canHostMeeting } from "@/features/external-accounts/constants";
import { useExternalAccounts } from "@/features/external-accounts/hooks/use-external-accounts";
import { useOAuthPopup } from "@/features/external-accounts/hooks/use-oauth-popup";
import { useOAuthErrorMessage } from "@/features/external-accounts/oauth-errors";
import { useTranslation } from "@/features/utils/i18n";
import { useState } from "react";
import { toast } from "sonner";
import type { CustomFieldProps } from "./dynamic-form";

const NONE = "none";
const NEW_MEET = "new:google_meet";
/** Meetings on the performers' own Google accounts, with no account to fall back on. */
const PERFORMERS_ONLY = "meet:performers";

/**
 * The booking module's video meetings, picked as an integration like the booking
 * software above it: none, or a Google Meet account — with "Nouveau compte Google
 * Meet" to connect one in place (Google, asked for its calendar events only, so
 * nothing gets synced).
 *
 * The account is where meetings go **when the assigned performer has no Google
 * account of their own**: theirs comes first, since only a meeting's owner can
 * run it (backend `conference-account.ts`). Picking sets both fields the module
 * reads, `enable_video_link` and `video_account_id`.
 */
export function VideoIntegrationField({
  fieldName,
  value,
  inheritable = false,
  parentValue,
  formData = {},
  onChange,
  onChangeFields,
}: CustomFieldProps) {
  const { t } = useTranslation("tools");
  const clientId = useCurrentClient()?.client?.id || "";
  const { data: accounts = [] } = useExternalAccounts();
  const openOAuthPopup = useOAuthPopup();
  const oauthErrorMessage = useOAuthErrorMessage();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const isInheriting = inheritable && value === undefined;
  const enabled = formData.enable_video_link === true;
  const accountId = String((isInheriting ? parentValue : value) ?? "");
  const hosts = accounts.filter(canHostMeeting);
  const selected = enabled ? hosts.find((a) => a.id === accountId) : undefined;

  const set = (values: Record<string, unknown>) =>
    onChangeFields ? onChangeFields(values) : onChange(values.video_account_id);

  const connectMeet = async () => {
    try {
      // The URL is handed over unresolved: awaiting it first gets the popup
      // blocked (`useOAuthPopup`).
      const result = await openOAuthPopup(
        ExternalAccountsAPI.getAuthorizationUrl(clientId, "google", ["video"]),
      );
      if (!result.success || !result.accountId) {
        toast.error(oauthErrorMessage(result));
        return;
      }
      set({ enable_video_link: true, video_account_id: result.accountId });
      toast.success(t("tools.integrations.video.connected"));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("tools.integrations.video.connectError"),
      );
    }
  };

  const pick = (id: string) => {
    setOpen(false);
    if (id === NONE) set({ enable_video_link: false, video_account_id: "" });
    else if (id === NEW_MEET) void connectMeet();
    else if (id === PERFORMERS_ONLY)
      set({ enable_video_link: true, video_account_id: "" });
    else set({ enable_video_link: true, video_account_id: id });
  };

  const items: ChoiceItem[] = [
    {
      id: NONE,
      title: t("tools.integrations.video.none"),
      description: t("tools.integrations.video.noneDescription"),
    },
    {
      id: PERFORMERS_ONLY,
      title: t("tools.integrations.video.performersTitle"),
      description: t("tools.integrations.video.performersDescription"),
      leading: <IntegrationLogo id="google_meet" />,
    },
    ...hosts.map((account) => ({
      id: account.id,
      title: t("tools.integrations.video.meetAccount", {
        identifier: account.provider_identifier || account.name,
      }),
      description: account.name,
      leading: <IntegrationLogo id="google_meet" />,
    })),
    {
      id: NEW_MEET,
      title: t("tools.integrations.picker.newAccount", { name: "Google Meet" }),
      description: t("tools.integrations.video.newDescription"),
      leading: <IntegrationLogo id="google_meet" />,
    },
  ];

  return (
    <div className="space-y-2">
      <Label htmlFor={fieldName}>{t("tools.integrations.video.label")}</Label>
      <div className="flex">
        <ChoicePickerTrigger
          id={fieldName}
          onClick={() => setOpen(true)}
          placeholder={t("tools.integrations.video.none")}
          leading={
            enabled ? <IntegrationLogo id="google_meet" size="xs" /> : undefined
          }
          label={
            selected
              ? t("tools.integrations.video.meetAccount", {
                  identifier: selected.provider_identifier || selected.name,
                })
              : enabled
                ? "Google Meet"
                : t("tools.integrations.video.none")
          }
          meta={
            enabled && !selected
              ? t("tools.integrations.video.performersOnly")
              : undefined
          }
        />
      </div>
      {enabled && (
        <p className="text-xs text-muted-foreground">
          {t("tools.integrations.video.hint")}
        </p>
      )}

      <ChoicePicker
        open={open}
        onOpenChange={setOpen}
        title={t("tools.integrations.video.title")}
        description={t("tools.integrations.video.description")}
      >
        <ChoicePickerPanel
          items={items.filter(
            (item) =>
              !search.trim() ||
              `${item.title} ${item.description ?? ""}`
                .toLocaleLowerCase()
                .includes(search.trim().toLocaleLowerCase()),
          )}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t("tools.integrations.video.search")}
          onResetFilters={() => setSearch("")}
          value={enabled ? selected?.id || PERFORMERS_ONLY : NONE}
          listLabel={t("tools.integrations.video.title")}
          fitContent
          emptyText={t("tools.integrations.picker.empty")}
          onCancel={() => setOpen(false)}
          onConfirm={pick}
        />
      </ChoicePicker>
    </div>
  );
}
